{
  "intro": [
    {
      "type": "paragraph",
      "text": "Welcome to SuiteScanner."
    },
    {
      "type": "paragraph",
      "text": "These Terms of Service (“Terms”) govern your access to and use of the SuiteScanner website and related services (collectively, the “Platform”), including searches, reservations and purchases made through the Platform. Accommodation and any other travel-related services available through the Platform are referred to as “Travel Services”."
    },
    {
      "type": "paragraph",
      "text": "SuiteScanner is operated by Onhouse Technologies Private Limited (CIN U62099DL2025PTC454572), a company incorporated in India, with its registered office at Sector C Pocket 8, House No. 8522, Vasant Kunj, New Delhi – 110070, India (“SuiteScanner”, “we”, “us” or “our”)."
    },
    {
      "type": "paragraph",
      "text": "By accessing the Platform, creating an account or completing a booking, you agree to these Terms and any booking-specific terms shown to you before payment."
    },
    {
      "type": "paragraph",
      "text": "If you do not agree to these Terms, please do not use the Platform."
    }
  ],
  "summary": [
    {
      "type": "paragraph",
      "text": "Before getting into the full Terms:"
    },
    {
      "type": "bullet",
      "text": "Travel prices and availability can change until your booking is confirmed."
    },
    {
      "type": "bullet",
      "text": "Hotel and room information is supplied largely by hotels, wholesalers and other travel providers."
    },
    {
      "type": "bullet",
      "text": "Room dimensions are indicative and should not be treated as guaranteed architectural measurements."
    },
    {
      "type": "bullet",
      "text": "Some taxes and fees may be payable directly at the property rather than through SuiteScanner."
    },
    {
      "type": "bullet",
      "text": "Cancellation and refund conditions vary by individual rate and will be shown before booking."
    },
    {
      "type": "bullet",
      "text": "Special requests are not guaranteed unless expressly confirmed."
    },
    {
      "type": "bullet",
      "text": "A payment being authorised does not, by itself, mean that a booking has been confirmed."
    },
    {
      "type": "bullet",
      "text": "Your booking is confirmed when we issue a booking confirmation."
    },
    {
      "type": "paragraph",
      "text": "The sections below explain these points in detail."
    }
  ],
  "sections": [
    {
      "id": 1,
      "title": "About SuiteScanner",
      "blocks": [
        {
          "type": "paragraph",
          "text": "SuiteScanner is a travel technology and booking platform through which users can search for, compare and book accommodation and, where available, other travel-related services."
        },
        {
          "type": "paragraph",
          "text": "The inventory available through SuiteScanner may be provided by hotels, accommodation operators, wholesalers, distributors, booking systems and other travel providers (collectively, “Travel Providers”)."
        },
        {
          "type": "paragraph",
          "text": "Unless expressly stated otherwise, SuiteScanner does not own, manage or operate the hotels, accommodation or other travel services displayed on the Platform."
        },
        {
          "type": "paragraph",
          "text": "Our role is to facilitate the discovery, booking and servicing of Travel Services made available to us by Travel Providers."
        },
        {
          "type": "paragraph",
          "text": "Individual bookings may also be subject to additional rules, restrictions and conditions imposed by the applicable Travel Provider. Where those terms apply to your booking, relevant conditions will be displayed to you during the booking process."
        }
      ]
    },
    {
      "id": 2,
      "title": "Eligibility and your account",
      "blocks": [
        {
          "type": "paragraph",
          "text": "You must be legally capable of entering into a binding contract to make a booking through SuiteScanner."
        },
        {
          "type": "paragraph",
          "text": "If you make a booking for another person, you confirm that:"
        },
        {
          "type": "bullet",
          "text": "you are authorised to make the booking on their behalf;"
        },
        {
          "type": "bullet",
          "text": "the information you provide about them is accurate; and"
        },
        {
          "type": "bullet",
          "text": "you will make them aware of any terms, restrictions and policies applicable to their booking."
        },
        {
          "type": "paragraph",
          "text": "You are responsible for maintaining the security of your SuiteScanner account and for activity carried out using your account credentials."
        },
        {
          "type": "paragraph",
          "text": "Please notify us promptly if you believe your account has been accessed without authorisation."
        }
      ]
    },
    {
      "id": 3,
      "title": "Travel information on SuiteScanner",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Travel information displayed on SuiteScanner may include:"
        },
        {
          "type": "paragraph",
          "text": "hotel names and locations, star classifications, room names, photographs, descriptions, room sizes, bed configurations, occupancy limits, accessibility information, meal plans, amenities, cancellation policies, property policies, taxes, fees and other information relevant to a booking."
        },
        {
          "type": "paragraph",
          "text": "Much of this information originates from the hotel or from one or more Travel Providers rather than being created or independently measured by SuiteScanner."
        },
        {
          "type": "paragraph",
          "text": "We take reasonable steps to display the information supplied to us accurately and to keep information current."
        },
        {
          "type": "paragraph",
          "text": "However, hotels and Travel Providers may update information without notice, and information supplied to us may occasionally be incomplete, approximate, inconsistent or incorrect."
        },
        {
          "type": "paragraph",
          "text": "If we become aware of a material error, we may correct the information shown on the Platform."
        }
      ]
    },
    {
      "id": 4,
      "title": "Room descriptions and room characteristics",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Hotel rooms within the same room category are not necessarily identical."
        },
        {
          "type": "paragraph",
          "text": "Unless a particular characteristic is expressly stated as guaranteed in your booking confirmation, individual rooms may vary in matters such as:"
        },
        {
          "type": "bullet",
          "text": "layout;"
        },
        {
          "type": "bullet",
          "text": "furniture;"
        },
        {
          "type": "bullet",
          "text": "décor;"
        },
        {
          "type": "bullet",
          "text": "floor;"
        },
        {
          "type": "bullet",
          "text": "view;"
        },
        {
          "type": "bullet",
          "text": "exact dimensions;"
        },
        {
          "type": "bullet",
          "text": "orientation;"
        },
        {
          "type": "bullet",
          "text": "bathroom layout; or"
        },
        {
          "type": "bullet",
          "text": "positioning within the property."
        },
        {
          "type": "paragraph",
          "text": "Photographs are intended to provide a reasonable representation of the relevant property or room category but may not depict the exact room ultimately assigned to you."
        },
        {
          "type": "paragraph",
          "text": "Room names and characteristics are supplied through hotel and Travel Provider booking systems. Similar room categories may sometimes be described differently across different systems."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner uses supplier-provided room identifiers and mapping data to associate rates with corresponding room categories."
        },
        {
          "type": "paragraph",
          "text": "Before completing a booking, you should review the room name, occupancy, bed configuration, meal plan, cancellation conditions and any other characteristics that are important to you."
        }
      ]
    },
    {
      "id": 5,
      "title": "Room sizes",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Room sizes displayed on SuiteScanner are indicative."
        },
        {
          "type": "paragraph",
          "text": "Room dimensions are provided to us by hotels and third-party Travel Providers and are not independently measured by SuiteScanner."
        },
        {
          "type": "paragraph",
          "text": "Different hotels and Travel Providers may also use different measurement standards or methods when calculating room area. Measurements may therefore be approximate or may differ from the precise dimensions of the room ultimately assigned to you."
        },
        {
          "type": "paragraph",
          "text": "For example, a room displayed as 35 m² should generally be understood as indicating a room of approximately that size and category rather than guaranteeing an exact floor area of 35.00 m²."
        },
        {
          "type": "paragraph",
          "text": "A reasonable variation in stated room size does not necessarily mean that a different room category has been provided."
        },
        {
          "type": "paragraph",
          "text": "Where exact dimensions are particularly important to you, including because of accessibility requirements, mobility needs, equipment requirements or another material reason, you should contact SuiteScanner or the property before booking so that the information can be verified where possible."
        },
        {
          "type": "paragraph",
          "text": "Room-size information may pass through multiple providers before appearing on the Platform."
        }
      ]
    },
    {
      "id": 6,
      "title": "Hotel amenities and facilities",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Amenities shown on SuiteScanner are based on information supplied by the property or our Travel Providers."
        },
        {
          "type": "paragraph",
          "text": "Hotel facilities and services can occasionally become unavailable because of:"
        },
        {
          "type": "paragraph",
          "text": "maintenance, renovation, weather conditions, seasonality, government requirements, operational changes or other circumstances outside SuiteScanner's reasonable control."
        },
        {
          "type": "paragraph",
          "text": "Unless expressly guaranteed as part of the booked rate, the availability of a particular hotel facility does not form a guarantee that the facility will remain continuously available throughout your stay."
        },
        {
          "type": "paragraph",
          "text": "If a particular facility is essential to your trip, we recommend confirming its availability before booking."
        }
      ]
    },
    {
      "id": 7,
      "title": "Prices",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Travel prices are dynamic."
        },
        {
          "type": "paragraph",
          "text": "The price shown during an initial search reflects the rate available to SuiteScanner at that time."
        },
        {
          "type": "paragraph",
          "text": "A search result does not reserve inventory or guarantee that the same rate will remain available later."
        },
        {
          "type": "paragraph",
          "text": "Before your booking is completed, SuiteScanner may perform additional availability and pricing checks with the relevant Travel Provider."
        },
        {
          "type": "paragraph",
          "text": "If the applicable rate changes during the booking process, we will show you the updated amount before asking you to proceed where technically possible."
        }
      ]
    },
    {
      "id": 8,
      "title": "Taxes and property charges",
      "blocks": [
        {
          "type": "paragraph",
          "text": "The price payable through SuiteScanner will be shown during checkout."
        },
        {
          "type": "paragraph",
          "text": "Taxes identified by the relevant Travel Provider as included in the rate will be included in the amount presented as payable through SuiteScanner."
        },
        {
          "type": "paragraph",
          "text": "Certain properties or destinations impose additional charges that are not collected as part of the online booking price."
        },
        {
          "type": "paragraph",
          "text": "These can include, for example:"
        },
        {
          "type": "bullet",
          "text": "city taxes;"
        },
        {
          "type": "bullet",
          "text": "tourism taxes;"
        },
        {
          "type": "bullet",
          "text": "resort fees;"
        },
        {
          "type": "bullet",
          "text": "destination fees;"
        },
        {
          "type": "bullet",
          "text": "local government charges; or"
        },
        {
          "type": "bullet",
          "text": "other charges payable at the property."
        },
        {
          "type": "paragraph",
          "text": "Where information about such charges has been supplied to us, SuiteScanner will display them separately from the amount payable through SuiteScanner."
        },
        {
          "type": "paragraph",
          "text": "Unless expressly stated otherwise, these amounts must be paid directly to the property."
        }
      ]
    },
    {
      "id": 9,
      "title": "Currency and exchange rates",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Prices may be displayed in different currencies depending on the Platform, destination, Travel Provider or payment method."
        },
        {
          "type": "paragraph",
          "text": "Where SuiteScanner offers currency conversion for convenience, converted amounts may be estimates unless the converted currency is the actual transaction currency shown during checkout."
        },
        {
          "type": "paragraph",
          "text": "Your bank, card issuer or payment provider may independently apply:"
        },
        {
          "type": "bullet",
          "text": "foreign exchange conversion;"
        },
        {
          "type": "bullet",
          "text": "international transaction fees;"
        },
        {
          "type": "bullet",
          "text": "card fees; or"
        },
        {
          "type": "bullet",
          "text": "other charges."
        },
        {
          "type": "paragraph",
          "text": "These charges are outside SuiteScanner's control."
        }
      ]
    },
    {
      "id": 10,
      "title": "Availability and cached search results",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Hotel availability can change rapidly."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner may use caching and other technical methods to provide faster search results."
        },
        {
          "type": "paragraph",
          "text": "This means that some availability or pricing information shown during browsing may have been retrieved shortly before you viewed it rather than at that precise second."
        },
        {
          "type": "paragraph",
          "text": "Displaying a room on:"
        },
        {
          "type": "bullet",
          "text": "a search page;"
        },
        {
          "type": "bullet",
          "text": "a hotel page;"
        },
        {
          "type": "bullet",
          "text": "a saved trip;"
        },
        {
          "type": "bullet",
          "text": "a shortlist; or"
        },
        {
          "type": "bullet",
          "text": "another part of the Platform"
        },
        {
          "type": "paragraph",
          "text": "does not mean that the room has been reserved for you."
        },
        {
          "type": "paragraph",
          "text": "We perform further checks before or during the booking process."
        },
        {
          "type": "paragraph",
          "text": "Your booking is not confirmed until the booking process has completed successfully and SuiteScanner has issued a confirmation."
        }
      ]
    },
    {
      "id": 11,
      "title": "Guest information",
      "blocks": [
        {
          "type": "paragraph",
          "text": "You must provide complete and accurate information when making a booking."
        },
        {
          "type": "paragraph",
          "text": "Depending upon the Travel Service, this can include:"
        },
        {
          "type": "bullet",
          "text": "full legal name;"
        },
        {
          "type": "bullet",
          "text": "age or date of birth;"
        },
        {
          "type": "bullet",
          "text": "nationality or citizenship;"
        },
        {
          "type": "bullet",
          "text": "country of residence;"
        },
        {
          "type": "bullet",
          "text": "contact information; and"
        },
        {
          "type": "bullet",
          "text": "other information reasonably required by the Travel Provider."
        },
        {
          "type": "paragraph",
          "text": "Some hotel rates are available only to residents or citizens of particular countries or markets."
        },
        {
          "type": "paragraph",
          "text": "You must therefore provide accurate citizenship or residency information where requested."
        },
        {
          "type": "paragraph",
          "text": "Do not provide a country based merely on your current physical location, IP address, departure location or payment method if it does not accurately reflect the information requested."
        },
        {
          "type": "paragraph",
          "text": "Incorrect information may result in:"
        },
        {
          "type": "bullet",
          "text": "a rate becoming unavailable;"
        },
        {
          "type": "bullet",
          "text": "a change in price;"
        },
        {
          "type": "bullet",
          "text": "cancellation;"
        },
        {
          "type": "bullet",
          "text": "refusal of check-in;"
        },
        {
          "type": "bullet",
          "text": "additional amounts becoming payable; or"
        },
        {
          "type": "bullet",
          "text": "other consequences imposed by the Travel Provider."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner is not responsible for losses that arise because materially incorrect traveller information was supplied to us."
        }
      ]
    },
    {
      "id": 12,
      "title": "Children and occupancy",
      "blocks": [
        {
          "type": "paragraph",
          "text": "The definition of a child and applicable child pricing may vary between travel products."
        },
        {
          "type": "paragraph",
          "text": "For accommodation supplied through certain SuiteScanner Travel Providers, a child may be defined as a person aged 17 years or under."
        },
        {
          "type": "paragraph",
          "text": "You must enter the correct ages of children when requested."
        },
        {
          "type": "paragraph",
          "text": "Incorrect ages can cause pricing differences or affect whether a room permits the requested occupancy."
        },
        {
          "type": "paragraph",
          "text": "The applicable occupancy and age rules shown during your booking will apply to that reservation."
        }
      ]
    },
    {
      "id": 13,
      "title": "Meal plans",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Where a booking includes a meal plan, the inclusion will be shown as part of the applicable room rate."
        },
        {
          "type": "paragraph",
          "text": "Examples may include:"
        },
        {
          "type": "bullet",
          "text": "room only;"
        },
        {
          "type": "bullet",
          "text": "breakfast;"
        },
        {
          "type": "bullet",
          "text": "half board;"
        },
        {
          "type": "bullet",
          "text": "full board; or"
        },
        {
          "type": "bullet",
          "text": "other property-specific meal arrangements."
        },
        {
          "type": "paragraph",
          "text": "The precise inclusions are determined by the relevant property and Travel Provider."
        },
        {
          "type": "paragraph",
          "text": "Where the hotel imposes separate rules relating to children's meals, beverages, dining times, restaurant availability or other meal-plan conditions, those rules may apply in addition to the general description shown on SuiteScanner."
        }
      ]
    },
    {
      "id": 14,
      "title": "Cancellation policies",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Every hotel rate has its own cancellation conditions."
        },
        {
          "type": "paragraph",
          "text": "Before completing a booking, SuiteScanner will display the cancellation conditions supplied for the selected rate."
        },
        {
          "type": "paragraph",
          "text": "Depending on the rate, a reservation may be:"
        },
        {
          "type": "bullet",
          "text": "cancellable without charge until a stated deadline;"
        },
        {
          "type": "bullet",
          "text": "partially refundable;"
        },
        {
          "type": "bullet",
          "text": "subject to cancellation penalties; or"
        },
        {
          "type": "bullet",
          "text": "non-refundable."
        },
        {
          "type": "paragraph",
          "text": "You should review these conditions carefully before paying."
        },
        {
          "type": "paragraph",
          "text": "Cancellation deadlines may be supplied by Travel Providers using a particular timezone and SuiteScanner may convert that deadline for display."
        },
        {
          "type": "paragraph",
          "text": "The cancellation deadline displayed for your confirmed booking should be treated as controlling unless we notify you otherwise."
        }
      ]
    },
    {
      "id": 15,
      "title": "Changes and modifications",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Whether a booking can be changed depends on the rules attached to the specific rate."
        },
        {
          "type": "paragraph",
          "text": "Requests to change:"
        },
        {
          "type": "bullet",
          "text": "dates;"
        },
        {
          "type": "bullet",
          "text": "guest names;"
        },
        {
          "type": "bullet",
          "text": "occupancy;"
        },
        {
          "type": "bullet",
          "text": "room type;"
        },
        {
          "type": "bullet",
          "text": "meal plan; or"
        },
        {
          "type": "bullet",
          "text": "other reservation details"
        },
        {
          "type": "paragraph",
          "text": "may require cancellation and rebooking rather than modification of the existing booking."
        },
        {
          "type": "paragraph",
          "text": "Changing a booking may also cause the original rate to become unavailable."
        },
        {
          "type": "paragraph",
          "text": "Where a modification results in a different price or supplier charge, we will communicate the applicable amount before proceeding where possible."
        }
      ]
    },
    {
      "id": 16,
      "title": "Special requests",
      "blocks": [
        {
          "type": "paragraph",
          "text": "You may sometimes be able to submit requests relating to matters such as:"
        },
        {
          "type": "bullet",
          "text": "adjoining rooms;"
        },
        {
          "type": "bullet",
          "text": "particular floors;"
        },
        {
          "type": "bullet",
          "text": "room location;"
        },
        {
          "type": "bullet",
          "text": "views;"
        },
        {
          "type": "bullet",
          "text": "bed preferences;"
        },
        {
          "type": "bullet",
          "text": "early check-in;"
        },
        {
          "type": "bullet",
          "text": "late check-out; or"
        },
        {
          "type": "bullet",
          "text": "accessibility requirements."
        },
        {
          "type": "paragraph",
          "text": "Unless SuiteScanner expressly states that a request has been guaranteed, special requests remain requests only."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner may transmit the request to the relevant Travel Provider or property, but fulfilment remains subject to availability and the property's discretion."
        },
        {
          "type": "paragraph",
          "text": "If a request is essential to your ability to travel or stay at the property, contact us before booking."
        }
      ]
    },
    {
      "id": 17,
      "title": "Booking confirmation",
      "blocks": [
        {
          "type": "paragraph",
          "text": "A booking is confirmed only when SuiteScanner has received a successful booking response from the applicable Travel Provider and issued you a booking confirmation."
        },
        {
          "type": "paragraph",
          "text": "Merely:"
        },
        {
          "type": "bullet",
          "text": "clicking a payment button;"
        },
        {
          "type": "bullet",
          "text": "authorising payment;"
        },
        {
          "type": "bullet",
          "text": "receiving a bank notification;"
        },
        {
          "type": "bullet",
          "text": "having funds temporarily blocked; or"
        },
        {
          "type": "bullet",
          "text": "reaching the final stage of checkout"
        },
        {
          "type": "paragraph",
          "text": "does not necessarily mean that the Travel Service has been successfully booked."
        },
        {
          "type": "paragraph",
          "text": "Travel booking systems involve multiple real-time systems and a booking request can occasionally fail or remain pending after payment authorisation."
        },
        {
          "type": "paragraph",
          "text": "If this happens, do not immediately make a duplicate reservation unless SuiteScanner confirms that the original booking failed."
        },
        {
          "type": "paragraph",
          "text": "We may need to verify the final status with the Travel Provider before determining whether the booking succeeded."
        }
      ]
    },
    {
      "id": 18,
      "title": "Payments",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Payments may be processed by third-party payment service providers."
        },
        {
          "type": "paragraph",
          "text": "By submitting payment details, you authorise SuiteScanner and the relevant payment service provider to process the amount shown during checkout."
        },
        {
          "type": "paragraph",
          "text": "Payments may be subject to fraud screening and additional authentication, including 3D Secure (3DS)."
        },
        {
          "type": "paragraph",
          "text": "A transaction may be declined or require additional verification by:"
        },
        {
          "type": "bullet",
          "text": "SuiteScanner;"
        },
        {
          "type": "bullet",
          "text": "our payment provider;"
        },
        {
          "type": "bullet",
          "text": "your card issuer;"
        },
        {
          "type": "bullet",
          "text": "a bank;"
        },
        {
          "type": "bullet",
          "text": "the Travel Provider; or"
        },
        {
          "type": "bullet",
          "text": "another participant in the payment process."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner is not responsible for a payment being declined by your bank or card issuer."
        }
      ]
    },
    {
      "id": 19,
      "title": "Payment successful but booking unsuccessful",
      "blocks": [
        {
          "type": "paragraph",
          "text": "In unusual circumstances, payment may be successfully captured or authorised while the underlying reservation cannot subsequently be confirmed."
        },
        {
          "type": "paragraph",
          "text": "Examples can include:"
        },
        {
          "type": "bullet",
          "text": "the room selling out;"
        },
        {
          "type": "bullet",
          "text": "a Travel Provider rejecting the booking;"
        },
        {
          "type": "bullet",
          "text": "a rate changing;"
        },
        {
          "type": "bullet",
          "text": "a booking timeout;"
        },
        {
          "type": "bullet",
          "text": "a technical failure; or"
        },
        {
          "type": "bullet",
          "text": "an inconsistent booking response."
        },
        {
          "type": "paragraph",
          "text": "If SuiteScanner determines that payment has been collected but the Travel Service cannot be confirmed, we will either:"
        },
        {
          "type": "bullet",
          "text": "offer an appropriate alternative where you agree to it; or"
        },
        {
          "type": "bullet",
          "text": "initiate a reversal or refund of the affected amount."
        },
        {
          "type": "paragraph",
          "text": "We will not intentionally retain payment for a Travel Service that was not successfully confirmed."
        }
      ]
    },
    {
      "id": 20,
      "title": "Refunds",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Your entitlement to a refund depends on:"
        },
        {
          "type": "bullet",
          "text": "the cancellation policy applying to your booking;"
        },
        {
          "type": "bullet",
          "text": "whether the Travel Service was successfully provided;"
        },
        {
          "type": "bullet",
          "text": "any applicable supplier charges; and"
        },
        {
          "type": "bullet",
          "text": "rights that cannot be excluded under applicable law."
        },
        {
          "type": "paragraph",
          "text": "Where a refund is due, SuiteScanner will generally return the relevant amount to the original payment method unless another method is agreed."
        },
        {
          "type": "paragraph",
          "text": "Once SuiteScanner has submitted a refund, additional processing time may be required by payment processors, card networks or financial institutions before the funds appear in your account."
        },
        {
          "type": "paragraph",
          "text": "These external processing times are outside SuiteScanner's control."
        }
      ]
    },
    {
      "id": 21,
      "title": "Problems with a property or stay",
      "blocks": [
        {
          "type": "paragraph",
          "text": "The property is responsible for operating the accommodation and providing on-site hotel services."
        },
        {
          "type": "paragraph",
          "text": "Matters primarily controlled by the property can include:"
        },
        {
          "type": "bullet",
          "text": "housekeeping;"
        },
        {
          "type": "bullet",
          "text": "maintenance;"
        },
        {
          "type": "bullet",
          "text": "food and beverage services;"
        },
        {
          "type": "bullet",
          "text": "property staff;"
        },
        {
          "type": "bullet",
          "text": "hotel facilities;"
        },
        {
          "type": "bullet",
          "text": "room assignment;"
        },
        {
          "type": "bullet",
          "text": "construction or renovation;"
        },
        {
          "type": "bullet",
          "text": "noise;"
        },
        {
          "type": "bullet",
          "text": "local operations; and"
        },
        {
          "type": "bullet",
          "text": "temporary closures."
        },
        {
          "type": "paragraph",
          "text": "If something materially differs from your confirmed reservation, please notify both the hotel and SuiteScanner as soon as reasonably possible."
        },
        {
          "type": "paragraph",
          "text": "Where possible, complaints relating to the stay should be raised while you are still at the property, because this gives the hotel and SuiteScanner the best opportunity to investigate and resolve the issue."
        }
      ]
    },
    {
      "id": 22,
      "title": "Material booking discrepancies",
      "blocks": [
        {
          "type": "paragraph",
          "text": "If the Travel Service you receive materially differs from what was confirmed, contact us promptly."
        },
        {
          "type": "paragraph",
          "text": "Examples might include:"
        },
        {
          "type": "bullet",
          "text": "a materially different room category;"
        },
        {
          "type": "bullet",
          "text": "an included meal plan not being honoured;"
        },
        {
          "type": "bullet",
          "text": "the property having no record of a confirmed reservation; or"
        },
        {
          "type": "bullet",
          "text": "another material element expressly included in the confirmed booking not being provided."
        },
        {
          "type": "paragraph",
          "text": "We may request reasonable evidence such as photographs, written confirmation from the property, receipts or other documentation needed to investigate the matter with the Travel Provider."
        },
        {
          "type": "paragraph",
          "text": "Nothing in these Terms prevents you from exercising rights you may have under mandatory applicable consumer law."
        }
      ]
    },
    {
      "id": 23,
      "title": "Accommodation descriptions and supplier errors",
      "blocks": [
        {
          "type": "paragraph",
          "text": "SuiteScanner will use reasonable care to present material booking information accurately."
        },
        {
          "type": "paragraph",
          "text": "However, much of the information displayed on the Platform is supplied by third parties."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner does not independently inspect every hotel, photograph every room, measure every room, test every amenity or independently verify every item of hotel content before displaying it."
        },
        {
          "type": "paragraph",
          "text": "Accordingly, to the extent permitted by law, SuiteScanner is not responsible for an error in information supplied by a Travel Provider where:"
        },
        {
          "type": "bullet",
          "text": "SuiteScanner displayed the information substantially as it was provided;"
        },
        {
          "type": "bullet",
          "text": "SuiteScanner could not reasonably have identified the error beforehand; and"
        },
        {
          "type": "bullet",
          "text": "the error was not caused by SuiteScanner's own act or omission."
        },
        {
          "type": "paragraph",
          "text": "If we become aware that material information is incorrect, we may correct the information and, where appropriate, contact affected customers."
        }
      ]
    },
    {
      "id": 24,
      "title": "Technical availability",
      "blocks": [
        {
          "type": "paragraph",
          "text": "We aim to make SuiteScanner available reliably, but we do not guarantee uninterrupted access to the Platform or to third-party travel inventory."
        },
        {
          "type": "paragraph",
          "text": "Searches and bookings depend upon networks and systems operated by Travel Providers and other third parties."
        },
        {
          "type": "paragraph",
          "text": "Services may occasionally be unavailable or interrupted because of:"
        },
        {
          "type": "bullet",
          "text": "maintenance;"
        },
        {
          "type": "bullet",
          "text": "supplier downtime;"
        },
        {
          "type": "bullet",
          "text": "network problems;"
        },
        {
          "type": "bullet",
          "text": "technical faults;"
        },
        {
          "type": "bullet",
          "text": "excessive demand;"
        },
        {
          "type": "bullet",
          "text": "cybersecurity incidents;"
        },
        {
          "type": "bullet",
          "text": "changes to third-party systems; or"
        },
        {
          "type": "bullet",
          "text": "circumstances outside our reasonable control."
        },
        {
          "type": "paragraph",
          "text": "If a technical issue occurs during a booking, please check your booking status or contact us before making a duplicate reservation."
        }
      ]
    },
    {
      "id": 25,
      "title": "Events outside reasonable control",
      "blocks": [
        {
          "type": "paragraph",
          "text": "SuiteScanner is not responsible for failure or delay caused by circumstances outside our reasonable control."
        },
        {
          "type": "paragraph",
          "text": "These may include:"
        },
        {
          "type": "bullet",
          "text": "extreme weather;"
        },
        {
          "type": "bullet",
          "text": "natural disasters;"
        },
        {
          "type": "bullet",
          "text": "war or civil disturbance;"
        },
        {
          "type": "bullet",
          "text": "government actions;"
        },
        {
          "type": "bullet",
          "text": "strikes;"
        },
        {
          "type": "bullet",
          "text": "epidemics or public-health restrictions;"
        },
        {
          "type": "bullet",
          "text": "transport disruption;"
        },
        {
          "type": "bullet",
          "text": "power failures;"
        },
        {
          "type": "bullet",
          "text": "internet or telecommunications failures;"
        },
        {
          "type": "bullet",
          "text": "major supplier system failures; or"
        },
        {
          "type": "bullet",
          "text": "other similar events."
        },
        {
          "type": "paragraph",
          "text": "Where such circumstances affect a booking, available refunds, cancellations or alternatives may depend upon the relevant Travel Provider's policy and applicable mandatory law."
        }
      ]
    },
    {
      "id": 26,
      "title": "Travel documents and entry requirements",
      "blocks": [
        {
          "type": "paragraph",
          "text": "You are responsible for ensuring that you and all travellers included in your booking hold any documents necessary for the trip."
        },
        {
          "type": "paragraph",
          "text": "Depending upon your destination, these can include:"
        },
        {
          "type": "bullet",
          "text": "passports;"
        },
        {
          "type": "bullet",
          "text": "visas;"
        },
        {
          "type": "bullet",
          "text": "transit permissions;"
        },
        {
          "type": "bullet",
          "text": "health documents;"
        },
        {
          "type": "bullet",
          "text": "permits; or"
        },
        {
          "type": "bullet",
          "text": "other entry documentation."
        },
        {
          "type": "paragraph",
          "text": "You are also responsible for checking applicable immigration, visa, health and entry requirements before travel."
        },
        {
          "type": "paragraph",
          "text": "Unless expressly stated otherwise, SuiteScanner does not provide immigration, visa or legal advice."
        }
      ]
    },
    {
      "id": 27,
      "title": "Fraud and misuse",
      "blocks": [
        {
          "type": "paragraph",
          "text": "You must not use SuiteScanner:"
        },
        {
          "type": "bullet",
          "text": "fraudulently;"
        },
        {
          "type": "bullet",
          "text": "for unlawful purposes;"
        },
        {
          "type": "bullet",
          "text": "to make bookings using stolen or unauthorised payment methods;"
        },
        {
          "type": "bullet",
          "text": "to interfere with the Platform or connected systems;"
        },
        {
          "type": "bullet",
          "text": "to circumvent access controls;"
        },
        {
          "type": "bullet",
          "text": "to scrape or extract data at scale without permission;"
        },
        {
          "type": "bullet",
          "text": "to make intentionally false reservations;"
        },
        {
          "type": "bullet",
          "text": "to exploit pricing or technical errors; or"
        },
        {
          "type": "bullet",
          "text": "in any way that materially disrupts the Platform or Travel Providers."
        },
        {
          "type": "paragraph",
          "text": "We may suspend or restrict an account, stop a transaction or cancel a booking where we reasonably believe fraudulent or unlawful activity is taking place, subject to applicable law and any refund rights that apply."
        }
      ]
    },
    {
      "id": 28,
      "title": "Obvious pricing or system errors",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Although we aim to display prices accurately, obvious pricing or technical errors can occur."
        },
        {
          "type": "paragraph",
          "text": "Examples include a hotel normally costing ₹50,000 per night being accidentally displayed as ₹500 because of a system or supplier error."
        },
        {
          "type": "paragraph",
          "text": "Where a price is clearly erroneous and a reasonable person would recognise that an error has occurred, SuiteScanner may cancel the affected transaction rather than being required to provide the Travel Service at the erroneous price."
        },
        {
          "type": "paragraph",
          "text": "If we cancel a booking for this reason after collecting payment, the amount paid for the affected booking will be refunded."
        },
        {
          "type": "paragraph",
          "text": "This provision does not allow SuiteScanner to cancel a valid booking merely because the price later becomes less commercially attractive."
        }
      ]
    },
    {
      "id": 29,
      "title": "Intellectual property",
      "blocks": [
        {
          "type": "paragraph",
          "text": "The Platform and its underlying software, design, branding, text and other proprietary content are owned by or licensed to SuiteScanner."
        },
        {
          "type": "paragraph",
          "text": "Hotel photographs, descriptions, trademarks and other third-party content may belong to the relevant Travel Provider, hotel or rights holder."
        },
        {
          "type": "paragraph",
          "text": "You may use the Platform for your personal travel-planning and booking purposes."
        },
        {
          "type": "paragraph",
          "text": "You may not reproduce, commercially redistribute, systematically extract or exploit Platform content except where permitted by law or expressly authorised by SuiteScanner."
        }
      ]
    },
    {
      "id": 30,
      "title": "Third-party services",
      "blocks": [
        {
          "type": "paragraph",
          "text": "The Platform may contain links to or integrations with third-party services."
        },
        {
          "type": "paragraph",
          "text": "Third-party services have their own terms and privacy practices."
        },
        {
          "type": "paragraph",
          "text": "SuiteScanner is not responsible for an independent third-party service merely because the Platform links to or integrates with that service."
        },
        {
          "type": "paragraph",
          "text": "This does not affect SuiteScanner's responsibility for services that we expressly agree to provide to you."
        }
      ]
    },
    {
      "id": 31,
      "title": "Privacy",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Our collection and use of personal information is governed by our Privacy Policy."
        },
        {
          "type": "paragraph",
          "text": "Information required to complete a booking may need to be shared with relevant Travel Providers, hotels, payment providers and other parties involved in fulfilling your reservation."
        },
        {
          "type": "paragraph",
          "text": "Please review our Privacy Policy for further information."
        }
      ]
    },
    {
      "id": 32,
      "title": "Our responsibility to you",
      "blocks": [
        {
          "type": "paragraph",
          "text": "SuiteScanner will provide its services with reasonable care and skill."
        },
        {
          "type": "paragraph",
          "text": "We are responsible for losses that are a reasonably foreseeable result of our breach of these Terms or failure to exercise reasonable care and skill."
        },
        {
          "type": "paragraph",
          "text": "To the maximum extent permitted by applicable law, SuiteScanner is not responsible for:"
        },
        {
          "type": "bullet",
          "text": "losses caused solely by information incorrectly supplied to us by you;"
        },
        {
          "type": "bullet",
          "text": "losses caused solely by a Travel Provider where SuiteScanner has fulfilled its own obligations with reasonable care;"
        },
        {
          "type": "bullet",
          "text": "circumstances outside our reasonable control;"
        },
        {
          "type": "bullet",
          "text": "indirect or consequential losses that were not reasonably foreseeable when you booked; or"
        },
        {
          "type": "bullet",
          "text": "business losses arising from a booking made primarily for personal travel."
        },
        {
          "type": "paragraph",
          "text": "Nothing in these Terms excludes or limits liability where doing so would be prohibited by applicable law."
        },
        {
          "type": "paragraph",
          "text": "In particular, nothing in these Terms is intended to exclude any mandatory consumer rights that apply to you."
        }
      ]
    },
    {
      "id": 33,
      "title": "Your responsibility",
      "blocks": [
        {
          "type": "paragraph",
          "text": "You are responsible for:"
        },
        {
          "type": "bullet",
          "text": "ensuring that booking information you submit is accurate;"
        },
        {
          "type": "bullet",
          "text": "reviewing the material terms of your booking before payment;"
        },
        {
          "type": "bullet",
          "text": "complying with the property's reasonable rules;"
        },
        {
          "type": "bullet",
          "text": "possessing required travel documents;"
        },
        {
          "type": "bullet",
          "text": "paying charges that are expressly identified as payable directly at the property; and"
        },
        {
          "type": "bullet",
          "text": "behaving lawfully when using the Platform."
        },
        {
          "type": "paragraph",
          "text": "You may be responsible for reasonable losses caused by fraudulent activity, deliberate misuse of the Platform or material information you knowingly provide incorrectly."
        }
      ]
    },
    {
      "id": 34,
      "title": "Communications",
      "blocks": [
        {
          "type": "paragraph",
          "text": "By making a booking, you agree that SuiteScanner may send transactional communications necessary to service that booking."
        },
        {
          "type": "paragraph",
          "text": "These may include:"
        },
        {
          "type": "bullet",
          "text": "booking confirmations;"
        },
        {
          "type": "bullet",
          "text": "payment notifications;"
        },
        {
          "type": "bullet",
          "text": "changes to reservations;"
        },
        {
          "type": "bullet",
          "text": "cancellation information;"
        },
        {
          "type": "bullet",
          "text": "check-in information;"
        },
        {
          "type": "bullet",
          "text": "refund updates; and"
        },
        {
          "type": "bullet",
          "text": "important service messages."
        },
        {
          "type": "paragraph",
          "text": "These transactional communications are separate from optional marketing communications."
        }
      ]
    },
    {
      "id": 35,
      "title": "Changes to these Terms",
      "blocks": [
        {
          "type": "paragraph",
          "text": "We may update these Terms from time to time."
        },
        {
          "type": "paragraph",
          "text": "The version displayed on the Platform at the time you make a booking will generally govern that booking, together with the booking-specific conditions presented to you."
        },
        {
          "type": "paragraph",
          "text": "Changes to these Terms will not retrospectively remove rights already attached to a confirmed booking unless required by law."
        },
        {
          "type": "paragraph",
          "text": "The latest revision date will appear at the top of this page."
        }
      ]
    },
    {
      "id": 36,
      "title": "If part of these Terms is invalid",
      "blocks": [
        {
          "type": "paragraph",
          "text": "If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue to apply to the fullest extent permitted by law."
        }
      ]
    },
    {
      "id": 37,
      "title": "No waiver",
      "blocks": [
        {
          "type": "paragraph",
          "text": "If SuiteScanner does not immediately enforce a provision of these Terms, that does not mean we have permanently waived our right to enforce it."
        }
      ]
    },
    {
      "id": 38,
      "title": "Governing law",
      "blocks": [
        {
          "type": "paragraph",
          "text": "These Terms are governed by the laws of India."
        },
        {
          "type": "paragraph",
          "text": "Subject to any mandatory consumer rights or jurisdictional protections that apply to you, disputes relating to these Terms or the use of SuiteScanner will be subject to the jurisdiction of the competent courts of New Delhi, India."
        },
        {
          "type": "paragraph",
          "text": "Nothing in this section deprives a consumer of a right to bring a claim in another jurisdiction where that right cannot lawfully be excluded by agreement."
        }
      ]
    },
    {
      "id": 39,
      "title": "Contact us",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Questions about a booking or these Terms can be sent to:"
        },
        {
          "type": "paragraph",
          "text": "Onhouse Technologies Private Limited\nSuiteScanner\nSector C Pocket 8, House No. 8522\nVasant Kunj, New Delhi – 110070\nIndia"
        },
        {
          "type": "paragraph",
          "text": "Customer support: customercare@suitescanner.com"
        },
        {
          "type": "paragraph",
          "text": "Grievance Officer: dd@suitescanner.com"
        },
        {
          "type": "paragraph",
          "text": "For booking-related issues, please include your SuiteScanner booking reference so that we can locate the reservation."
        }
      ]
    }
  ]
}
